import {useI18n} from '@yandex-delivery/core/i18n'
import {Typography} from '@yandex-delivery/ui'
import {Suspense, useMemo} from 'react'

import type {Tariff, TariffsZone} from '../../../../../../api'
import {ErrorBoundary} from '../../../../../../components/ErrorBoundary'
import {Loader, Paper} from '../../../../../../components/ui'
import {useConfig} from '../../../../../../config'
import {useFilters} from '../../../../../../hooks'
import type {PathWithCountry} from '../../../../../../types'
import {Resource} from '../../../../../../utils'
import {CargoDownloadButtonContainer} from '../../components/DownloadButton/CargoDownloadButton.container'
import {RetainPrevious} from '../../components/retain-previous'
import {TariffFilters} from '../../components/tariff-filters'
import {CargoInfo} from './CargoInfo'
import styleClasses from './classes.module.css'

type Props = {
  city: TariffsZone
  tariff: Tariff
  path: PathWithCountry
}

export function CargoInfoContainer(props: Props) {
  const {city, tariff, path} = props
  const {tariffsService} = useConfig()
  const {filters} = useFilters()

  const resource = useMemo(
    () => new Resource(() => tariffsService.fetchCargoInfo(path.country, city.id, tariff.id, filters)),
    [city.id, path.country, tariff.id, tariffsService, filters],
  )

  const i18n = useI18n()

  return (
    <div className={styleClasses.cargoInfoContainer}>
      <Paper className={styleClasses.infoHeader}>
        <div className={styleClasses.infoHeaderTitle}>
          <Typography density="tight" level="title2" weight="bold">
            {i18n('tariffs-v3.tariff-info.title', {
              placeholder: {
                tariffTitle: tariff.title,
                cityTitle: city.title,
              },
            })}
          </Typography>
          <CargoDownloadButtonContainer country={path.country} tariff={tariff.id} zone={city.id} />
        </div>
        <Suspense fallback={null}>
          <RetainPrevious resource={resource}>
            {data => (data.filters.length > 0 ? <TariffFilters tariffsCargoInfo={data} /> : null)}
          </RetainPrevious>
        </Suspense>
      </Paper>

      <div className={styleClasses.contentWrapper}>
        <ErrorBoundary>
          <Suspense fallback={<Loader />}>
            <CargoInfo {...props} resource={resource} />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  )
}
